import { DialogComponent } from '@theme/dialog';

/** Read-only sizing help. Never selects variants or changes product forms. */
class LanoelRingGuide extends DialogComponent {
  #openingFrame = 0;

  showDialog() {
    if (this.refs.dialog.open) return;
    super.showDialog();
    this.#openingFrame = requestAnimationFrame(() => {
      this.refs.dialog.scrollTop = 0;
      this.#openingFrame = 0;
    });
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.#openingFrame);
    super.disconnectedCallback();
  }

  /** @param {KeyboardEvent} event */
  handleMeasurementKey(event) {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    this.calculate();
  }

  calculate() {
    const input = this.querySelector('[ref="measurement"]');
    const result = this.querySelector('[ref="result"]');
    const productResult = this.querySelector('[ref="productResult"]');
    const messages = this.querySelector('[data-ring-messages]');
    if (!(input instanceof HTMLInputElement) || !result || !messages) return;

    const text = JSON.parse(messages.textContent || '{}');
    const fill = (key, values = {}) => (text[key] || '').replace(/\{(\w+)\}/g, (_, name) => values[name] ?? '');
    const format = (number) => number.toLocaleString('de-DE', { maximumFractionDigits: 2 });
    const raw = input.value.trim().replace(',', '.');
    const circumference = Number(raw);
    for (const row of this.querySelectorAll('[data-highlight]')) row.removeAttribute('data-highlight');
    if (productResult) productResult.textContent = '';
    input.removeAttribute('aria-invalid');

    if (!raw) {
      result.textContent = fill('initial');
      return;
    }
    if (!/^\d{2}(?:\.\d{1,2})?$/.test(raw) || !Number.isFinite(circumference) || circumference < 40 || circumference > 80) {
      input.setAttribute('aria-invalid', 'true');
      result.textContent = fill('invalid');
      return;
    }

    const lower = Math.floor(circumference);
    const upper = Math.ceil(circumference);
    if (circumference < 48 || circumference > 68) {
      result.textContent = fill('outside');
    } else {
      result.textContent = fill(lower === upper ? 'exact' : 'between', {
        size: lower, lower, upper, diameter: format(circumference / Math.PI),
      });
      for (const row of this.querySelectorAll('[data-ring-eu]')) {
        if ([lower, upper].includes(Number(row.dataset.ringEu))) row.setAttribute('data-highlight', '');
      }
    }

    // Only explicitly checked model measurements are eligible for matching.
    const options = [...this.querySelectorAll('[data-ring-model-size]')].map((row) => ({
      row, label: row.dataset.ringModelSize, circumference: Number(row.dataset.circumference),
    })).sort((a, b) => a.circumference - b.circumference);
    if (!productResult || !options.length) return;
    const exact = options.find((option) => Math.abs(option.circumference - circumference) < 0.05);
    if (exact) {
      exact.row.setAttribute('data-highlight', '');
      productResult.textContent = fill('match', { size: exact.label, circumference: format(exact.circumference) });
      return;
    }
    const below = options.filter((option) => option.circumference < circumference).at(-1);
    const above = options.find((option) => option.circumference > circumference);
    if (!below || !above) {
      productResult.textContent = fill('product_outside');
      return;
    }
    below.row.setAttribute('data-highlight', '');
    above.row.setAttribute('data-highlight', '');
    productResult.textContent = fill('product_between', { lower: below.label, upper: above.label });
  }
}

if (!customElements.get('lanoel-ring-guide')) {
  customElements.define('lanoel-ring-guide', LanoelRingGuide);
}
